import React from 'react';

import { useCalendarStore, useTimezone } from '@uplift/contexts';


import { DatesSetArg } from '@fullcalendar/react';
import { CalendarDateService, DateObject } from '@uplift/services';
import { CalendarViewTypes } from '@uplift/types';
import { CalendarRef } from '@uplift/ui-v2';


export const useCalendarSynchronizationDate = (calendarRef: React.MutableRefObject<CalendarRef>) => {
  const timezone = useTimezone();
  const calendarStore = useCalendarStore();
  const isTodayRef = React.useRef<boolean>(false);

  const [ date, _setDate ] = React.useState<DateObject>(() => CalendarDateService.date().utc().tz(timezone, true));
  const lastDateRef = React.useRef<DateObject>(date);
  const setDate = (_newDate: DateObject) => {
    const newDate = _newDate.tz(timezone, true);

    if (lastDateRef.current?.toISOString() === _newDate?.toISOString()) {
      return;
    }

    lastDateRef.current = newDate;
    calendarStore.calendarApi?.gotoDate(newDate.format());
    _setDate(newDate);
  };

  React.useEffect(() => {
    const handler = (args: DatesSetArg) => {
      const weekStartDate = lastDateRef.current.day();
      const start = CalendarDateService.date(args.start).tz(timezone);
      let newDate: DateObject;

      if (isTodayRef.current) {
        newDate = CalendarDateService.date().tz(timezone);
      } else if (args.view.type === CalendarViewTypes.timeGridWeek) {
        const startOfWeek = start.set('day', 0);

        newDate = startOfWeek.add(weekStartDate, 'day');
      } else if (args.view.type === CalendarViewTypes.timeGridDay) {
        newDate = start;
      }

      setDate(newDate);
      lastDateRef.current = newDate;
      isTodayRef.current = false;
    };

    const todayHandler = () => {
      isTodayRef.current = true;
    };

    const emitter = calendarRef.current?.customEmitter;

    emitter.on('today', todayHandler);
    calendarStore.calendarApi?.on('datesSet', handler);

    return () => {
      calendarStore.calendarApi?.off('datesSet', handler);
      emitter.off('today', todayHandler);
    };
  }, [ calendarStore.calendarApi, timezone ]); // eslint-disable-line react-hooks/exhaustive-deps

  return [ date, setDate ] as const;
};
